import { normalizeEntry } from '@/features/editor/lib/editor-normalize'
import { normalizeTags } from '@/features/editor/lib/editor-tags'
import { resolvePresetCode } from '@/features/editor/lib/preset'
import type {
  EditorDraft,
  EditorKnowledge,
  EditorPublishStatus,
  EditorReviewMeta,
  EditorEngineDetail,
} from '@/features/editor/types/editor.types'
import type {
  KnowledgeEntry,
  RetrievalMethod,
  KeywordTriggerOp,
} from '@/features/editor/types/knowledge.types'

// ====== 前端默认值（后端未来接入再改） ======
const DEFAULT_KEYWORD_OP: KeywordTriggerOp = 'or'
// ==========================================

function str(v: unknown): string {
  return typeof v === 'string' ? v : ''
}

function trimmedOrEmpty(v: unknown): string {
  const s = str(v).trim()
  return s.length ? s : ''
}

function bool(v: unknown): boolean {
  return Boolean(v)
}

function keywordsArray(v: unknown): string[] {
  return Array.isArray(v) ? v.filter((x) => typeof x === 'string') : []
}

function asRetrievalMethod(v: unknown): RetrievalMethod {
  return v === 'prompt' ? 'prompt' : 'permanent'
}

function asKeywordOp(v: unknown): KeywordTriggerOp {
  return v === 'and' ? 'and' : 'or'
}

function mapEntry(raw: unknown): KnowledgeEntry {
  const r = (raw ?? {}) as Record<string, unknown>
  const retrievalMethod = asRetrievalMethod(r.retrievalMethod)

  return normalizeEntry({
    id: str(r.id),
    title: str(r.title),
    content: str(r.content),
    enabled: bool(r.enabled),

    retrievalMethod,

    keywords: retrievalMethod === 'prompt' ? keywordsArray(r.keywords) : [],

    keywordOp: asKeywordOp(
      r.keywordLogic ?? r.keywordOp ?? DEFAULT_KEYWORD_OP
    ),
    isUserCard: bool(r.isUserCard),
  })
}

/**
 * Engine detail (unknown) -> Editor state
 */
export function mapEngineDetailToEditorState(raw: unknown): {
  draft: EditorDraft
  knowledge: Partial<EditorKnowledge>
  publishStatus: EditorPublishStatus
  reviewMeta: EditorReviewMeta
} {
  const data = (raw as { data?: { engine?: unknown } })?.data
  const engine = (data?.engine ?? {}) as EditorEngineDetail

  const publishStatus: EditorPublishStatus =
    engine?.status === 'published' ? 'published' : 'draft'

  const draft: EditorDraft = {
    engineId: engine?.id ?? null,

    title: str(engine?.title ?? engine?.name),
    description: str(engine?.description),
    tags: normalizeTags(engine?.tags),

    coverImageUrl: trimmedOrEmpty(engine?.imageUrl),
    backgroundImageUrl: trimmedOrEmpty(engine?.backgroundImageUrl),

    narrativeCore: str(engine?.settings?.narrativeCore),
    firstMessage: str(engine?.settings?.firstMessage ?? engine?.firstMessage),

    promptTemplateMode: resolvePresetCode(engine?.presetTemplateId),
  } as EditorDraft

  const knowledge: Partial<EditorKnowledge> = {
    worldInfo: Array.isArray(engine?.worldInfo)
      ? engine.worldInfo.map(mapEntry)
      : [],
    characterProfiles: Array.isArray(engine?.characterProfiles)
      ? engine.characterProfiles.map(mapEntry)
      : [],
  }

  const reviewMeta: EditorReviewMeta = {
    lastReviewNote: trimmedOrEmpty(engine?.lastReviewNote) || undefined,
    lastReviewedAt: trimmedOrEmpty(engine?.lastReviewedAt) || undefined,
    updatedAt: trimmedOrEmpty(engine?.updatedAt) || undefined,
    hasPendingReview: bool(engine?.hasPendingReview),
  }

  return { draft, knowledge, publishStatus, reviewMeta }
}
